import React, { JSX, useEffect, useState } from "react";
import "../../styles/Authentication.scss";
import { useLabelsContext } from "../../context/LabelsContext";
import { motion } from "framer-motion";
import { fadeScale } from "../../styles/MotionFramerAnimations";
import { useConfigContext } from "../../context/ConfigContext";
import { OtpMethodSelector } from "./OtpMethodSelector";
import { useUserContext } from "../../context/UserContext";
import { sendOTP } from "../../services/auth-service";
import { useClubContext } from "../../context/ClubContext";
import { parseBoolean } from "../../utils/helpers";
import { ApiResponse, ApiStatus } from "../../models/api-response";
import { LoginFormHeader } from "./LoginFormHeader";
import { useLoginForm } from "../../hooks/useLoginForm";
import { useAuthContext } from "../../context/AuthContext";
import { SwitchLoginType } from "./SwitchLoginType";
import { LOGIN_METHODS } from "../../models/login-methods";
import { JoinFree } from "./JoinFree";

type LoginFormProps = {
	handleAuthFlow: () => void;
	setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	isLoading: boolean;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export const LoginForm: React.FC<LoginFormProps> = ({
	handleAuthFlow,
	isLoading,
	setIsLoading,
}) => {
	const { club } = useClubContext();
	const { labels } = useLabelsContext();
	const { config } = useConfigContext();
	const { user, setUser } = useUserContext();
	const [canSwitchLoginType, setCanSwitchLoginType] =
		useState<boolean>(false);
	const {
		otpMethod,
		setOtpMethod,
		setOtpSentMessage,
		loginMethod,
		setLoginMethod,
		interestedUser,
	} = useAuthContext();

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		errors,
		setError,
		loginForm,
		getErrorMessage,
	} = useLoginForm();

	const renderErrorMessage = (): JSX.Element | null => {
		const fieldError =
			getErrorMessage(loginMethod) ??
			(errors.root?.serverError?.message
				? { key: "server", message: errors.root.serverError.message }
				: null);

		if (!fieldError) return null;

		return (
			<motion.div {...fadeScale} className='error-container'>
				<span className={`error-message key-${fieldError.key}`}>
					{fieldError.message}
				</span>
			</motion.div>
		);
	};

	const renderPassportField = (): JSX.Element => {
		return (
			<div className={`form-field ${errors.passport ? "has-error" : ""}`}>
				<input
					id='passport'
					type='number'
					placeholder=' '
					{...register("passport", loginForm.passport)}
					defaultValue={user?.Passport ?? undefined}
				/>
				<label className='form-label' htmlFor='passport'>
					{labels?.["passportCrm"]}
				</label>
			</div>
		);
	};

	const renderEmailField = (): JSX.Element => {
		return (
			<div className={`form-field ${errors.email ? "has-error" : ""}`}>
				<input
					id='email'
					type='email'
					placeholder=' '
					defaultValue={user && user.Email ? user?.Email : undefined}
					{...register("email", loginForm.email)}
				/>
				<label className='form-label' htmlFor='email'>
					{labels?.["Email"]}
				</label>
			</div>
		);
	};

	const switchLoginTypeAllowed = (): boolean => {
		if (loginMethod !== LOGIN_METHODS.PASSPORT) return false;

		return parseBoolean(
			labels?.["TryToSearchByEmailOnPassportLogin"] ?? "",
		);
	};

	const onSubmit = async () => {
		setIsLoading(true);
		const loginData: string = getValues(loginMethod) || "";

		sendOTP(config, otpMethod, loginMethod, loginData, club?.bookingDomain)
			.then((otpResponse: ApiResponse<string>) => {
				if (
					otpResponse.Status === ApiStatus.Error &&
					otpResponse.Errors?.length
				) {
					setError("root.serverError", {
						type: "server",
						message: otpResponse.Errors[0].Message,
					});

					if (switchLoginTypeAllowed()) {
						setCanSwitchLoginType(true);
					}
					return;
				}
				if (
					otpResponse.Status === ApiStatus.Success &&
					otpResponse.Data
				) {
					setOtpSentMessage(otpResponse.Data);

					if (loginMethod === LOGIN_METHODS.PASSPORT) {
						setUser({ ["Passport"]: loginData, LoggedIn: false });
					}
					if (loginMethod === LOGIN_METHODS.EMAIL) {
						setUser({ ["Email"]: loginData, LoggedIn: false });
					}
					if (loginMethod === LOGIN_METHODS.GUESTID) {
						setUser({ ["GuestId"]: loginData, LoggedIn: false });
					}
					handleAuthFlow();
				}
			})
			.catch(() => {
				setError("root.serverError", {
					type: "server",
					message: labels?.["ApiErrorsLabels1"],
				});
			})
			.finally(() => setIsLoading(false));
	};

	const handleSubmitClick = (
		e: React.MouseEvent<HTMLButtonElement>,
	): void => {
		// Safari can occasionally fail to dispatch a submit event on button click
		// (especially in embedded contexts). Trigger RHF submit directly.
		e.preventDefault();
		handleSubmit(onSubmit)();
	};

	return (
		<div className='login-form-container'>
			<LoginFormHeader />
			<form
				className='simplex-club-form'
				noValidate
				onSubmit={handleSubmit(onSubmit)}
			>
				<div className='form-col'>
					{renderErrorMessage()}
					{!interestedUser && (
						<>
							{loginMethod === LOGIN_METHODS.PASSPORT &&
								renderPassportField()}
							{loginMethod === LOGIN_METHODS.EMAIL &&
								renderEmailField()}

							{canSwitchLoginType && (
								<SwitchLoginType
									setValue={setValue}
									loginMethod={loginMethod}
									setLoginMethod={setLoginMethod}
									setCanSwitchLoginType={
										setCanSwitchLoginType
									}
								/>
							)}
						</>
					)}
					{config?.language === "he" && (
						<OtpMethodSelector
							otpMethod={otpMethod}
							setOtpMethod={setOtpMethod}
							isLoading={isLoading}
						/>
					)}
					<button
						type='submit'
						className='form-submit'
						disabled={isLoading}
						onClick={handleSubmitClick}
					>
						{isLoading && (
							<span className='loader secondary'></span>
						)}
						{labels?.["Continue"]}
					</button>
					{!interestedUser && (
						<div className='form-secondary-action'>
							<JoinFree />
						</div>
					)}
				</div>
			</form>
		</div>
	);
};
